import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import {
  WifiIcon,
  ServerIcon,
  CircleStackIcon,
  PlayIcon,
  StopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon
} from '@heroicons/react/24/outline';

const SignalKBrowser = () => {
  const { isAuthenticated } = useAuth();
  const [connections, setConnections] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectionData, setConnectionData] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadConnections();
      
      // Set up periodic status updates every 10 seconds
      const statusInterval = setInterval(() => {
        loadConnections();
      }, 10000);

      return () => clearInterval(statusInterval);
    }
  }, [isAuthenticated]);

  const loadConnections = async () => {
    try {
      const response = await api.get('/api/signalk-connections');
      const connectionsData = response.data.connections || [];
      setConnections(connectionsData);
      
      // Set first connection as active tab if none selected
      if (!activeTab && connectionsData.length > 0) {
        setActiveTab(connectionsData[0].id);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load SignalK connections');
    } finally {
      setLoading(false);
    }
  };

  const loadConnectionData = async (connectionId) => {
    try {
      setRefreshing(true);
      const connection = connections.find(c => c.id === connectionId);
      if (!connection || !connection.vessel_id) {
        toast.error('Connection not found or missing vessel ID');
        return;
      }
      
      const response = await api.get(`/api/signalk/data/${connection.vessel_id}`);
      setConnectionData(prev => ({
        ...prev,
        [connectionId]: response.data
      }));
    } catch (error) {
      console.error('Error loading connection data:', error);
      toast.error('Failed to load connection data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleConnect = async (connectionId) => {
    try {
      await api.post(`/api/signalk-connections/${connectionId}/connect`);
      toast.success('Connected successfully');
      loadConnections();
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error(error.response?.data?.error || 'Failed to connect');
    }
  };

  const handleDisconnect = async (connectionId) => {
    try {
      await api.post(`/api/signalk-connections/${connectionId}/disconnect`);
      toast.success('Disconnected successfully');
      loadConnections();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect');
    }
  };

  const getConnectionIcon = (type) => {
    switch (type) {
      case 'local': return WifiIcon;
      case 'vpn': return ServerIcon;
      case 'database': return CircleStackIcon;
      case 'demo': return SignalIcon;
      default: return WifiIcon;
    }
  };

  const getConnectionColor = (type) => {
    switch (type) {
      case 'local': return '#10b981'; // Green
      case 'vpn': return '#3b82f6';   // Blue
      case 'database': return '#ef4444'; // Red
      case 'demo': return '#8b5cf6'; // Purple
      default: return '#6b7280'; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return CheckCircleIcon;
      case 'available': return ClockIcon;
      case 'error': return XCircleIcon;
      default: return ExclamationTriangleIcon;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-100';
      case 'available': return 'text-blue-600 bg-blue-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const renderConnectionData = (connection) => {
    const data = connectionData[connection.id] || {};
    
    if (connection.status !== 'connected') {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <SignalIcon className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
          <p className="text-gray-500">
            {connection.status === 'error' 
              ? 'Connection error - check your settings'
              : 'Connect to start receiving data'
            }
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Real-time Data</h3>
          <button
            onClick={() => loadConnectionData(connection.id)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {data.data && data.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.data.slice(0, 12).map((item, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 truncate">{item.path}</h4>
                  <span className="text-xs text-gray-500">
                    {new Date(item.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {typeof item.value === 'object' 
                    ? JSON.stringify(item.value).substring(0, 50) + '...'
                    : item.value
                  }
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {item.unit || 'No unit'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <ClockIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-500">No data available yet. Data will appear here when available.</p>
          </div>
        )}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
              <SignalIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Access Denied
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please log in to view SignalK data
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading SignalK connections...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">SignalK Data Browser</h1>
          <p className="mt-2 text-gray-600">Explore real-time marine data from your vessels</p>
        </div>

        {connections.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <SignalIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No SignalK Connections</h3>
            <p className="text-gray-500 mb-6">
              Configure SignalK connections in settings to start browsing data
            </p>
            <button
              onClick={() => window.location.href = '/settings'}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Settings
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                {connections.map((connection) => {
                  const IconComponent = getConnectionIcon(connection.type);
                  const StatusIconComponent = getStatusIcon(connection.status);
                  
                  return (
                    <button
                      key={connection.id}
                      onClick={() => {
                        setActiveTab(connection.id);
                        loadConnectionData(connection.id);
                      }}
                      className={`${
                        activeTab === connection.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: getConnectionColor(connection.type) }} />
                      {connection.name}
                      <StatusIconComponent className="w-4 h-4" />
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab && connections.find(c => c.id === activeTab) ? (
                (() => {
                  const connection = connections.find(c => c.id === activeTab);
                  const IconComponent = getConnectionIcon(connection.type);
                  const StatusIconComponent = getStatusIcon(connection.status);
                  
                  return (
                    <div>
                      {/* Connection Header */}
                      <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                            style={{ backgroundColor: getConnectionColor(connection.type) }}
                          >
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div>
                            <h2 className="text-xl font-semibold text-gray-900">{connection.name}</h2>
                            <p className="text-sm text-gray-500 capitalize">{connection.type} Connection</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(connection.status)}`}>
                            <StatusIconComponent className="w-4 h-4" />
                            {connection.status || 'disconnected'}
                          </div>
                          
                          {connection.status === 'connected' ? (
                            <button
                              onClick={() => handleDisconnect(connection.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                              <StopIcon className="w-4 h-4" />
                              Disconnect
                            </button>
                          ) : (
                            <button
                              onClick={() => handleConnect(connection.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              <PlayIcon className="w-4 h-4" />
                              Connect
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Connection Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {connection.host && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="text-sm text-gray-500">Host</div>
                            <div className="font-mono text-lg">{connection.host}:{connection.port}</div>
                          </div>
                        )}
                        
                        {connection.latency && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="text-sm text-gray-500">Latency</div>
                            <div className="text-lg font-semibold">{connection.latency}ms</div>
                          </div>
                        )}
                        
                        {connection.data_quality && (
                          <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="text-sm text-gray-500">Data Quality</div>
                            <div className="text-lg font-semibold">{connection.data_quality}%</div>
                          </div>
                        )}
                      </div>

                      {/* Data Display */}
                      {renderConnectionData(connection)}
                    </div>
                  );
                })()
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Select a connection to view data</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignalKBrowser;
